import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormikErrors, FormikTouched } from 'formik';
import { InputField, SelectField } from '../../../components';
import FileUpload from './file-upload';
import { Card, CardContent } from '../../../components/ui/card';
import {
  toOptions,
  useReferenceData,
} from '../../../hooks/use-reference-data';
import {
  getCountryOptions,
  getPostalCodeLabelKey,
} from '../../../lib/countries';
import { getExampleNumber, type CountryCode } from '../../../lib/phone';

export interface BusinessFormValues {
  name: string;
  type: string;
  industryType: string;
  country: string;
  logo: File | null;
  email: string;
  phoneNumber: string;
  website: string;
  address: string;
  city: string;
  zipCode: string;
}

interface BusinessFormProps {
  values: BusinessFormValues;
  errors?: FormikErrors<BusinessFormValues>;
  touched?: FormikTouched<BusinessFormValues>;
  onFieldChange: (
    field: keyof BusinessFormValues,
    value: string | File | null
  ) => void;
  /** Prefilled from the verified account so the email box is never blank. */
  accountEmail?: string;
}

/**
 * The whole of onboarding, on one screen.
 *
 * This replaced a five-screen wizard that demanded eleven fields, a logo, a
 * legal entity name and a signed agreement before it would create a business
 * record — none of which the API ever required. Five things are asked for here.
 * Everything else moved to the settings page and the pre-launch checklist,
 * where the customer has a reason to fill it in.
 *
 * Every option list comes from `GET /reference-data`. Nothing on this screen is
 * a hardcoded array any more.
 */
export function BusinessForm({
  values,
  errors,
  touched,
  onFieldChange,
  accountEmail,
}: Readonly<BusinessFormProps>) {
  const { t, i18n } = useTranslation();

  // Legal entity forms depend on the country: a Singaporean sees "Pte Ltd", an
  // Indian sees "OPC". Refetches when the country changes.
  const { industries, businessTypes, isLoading } = useReferenceData(
    values.country || undefined
  );

  const countryOptions = useMemo(
    () => getCountryOptions(i18n.language),
    [i18n.language]
  );

  const industryOptions = useMemo(
    () => toOptions(t, 'industry', industries),
    [t, industries]
  );

  const businessTypeOptions = useMemo(
    () => toOptions(t, 'businessType', businessTypes),
    [t, businessTypes]
  );

  const showError = (field: keyof BusinessFormValues) =>
    touched?.[field] && errors?.[field]
      ? (errors[field] as string)
      : undefined;

  const phonePlaceholder = getExampleNumber(
    values.country as CountryCode | undefined
  );

  const postalLabel = t(getPostalCodeLabelKey(values.country));

  return (
    <div className="space-y-6 duration-300 animate-in fade-in">
      {/* InputField and SelectField are borderless "filled" controls: they carry
          `bg-background` (#F4F4F4) with `border-none`, so they are only legible
          against a white surface. Rendering them straight onto the page's
          `bg-background-main` (#F3F3FF) left them all but invisible. Every other
          form in the app puts them on a white panel; these do the same. */}
      <section className="p-6 space-y-5 bg-white rounded-lg shadow-sm">
        <header className="space-y-1">
          <h2 className="text-lg font-semibold">
            {t('accountSetup.aboutYourBusiness')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('accountSetup.aboutYourBusinessDescription')}
          </p>
        </header>

        <div className="space-y-2">
          <p className="text-sm font-medium">
            {t('accountSetup.businessLogo')}
            <span className="ml-1 text-destructive">*</span>
          </p>
          <FileUpload
            title={t('accountSetup.businessLogo')}
            description={t('accountSetup.businessLogoDescription')}
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            file={values.logo}
            error={showError('logo')}
            setFile={(file) => onFieldChange('logo', file)}
          />
        </div>

        <InputField
          name="name"
          label={t('accountSetup.businessName')}
          placeholder={t('accountSetup.businessNamePlaceholder')}
          required
          value={values.name}
          error={showError('name')}
          onChange={(event) => onFieldChange('name', event.target.value)}
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <SelectField
            name="country"
            label={t('accountSetup.country')}
            placeholder={t('accountSetup.countryPlaceholder')}
            required
            options={countryOptions}
            value={values.country}
            error={showError('country')}
            onChange={(value) => {
              onFieldChange('country', value);
              // The legal entity list is country-scoped, so a previously chosen
              // type may not exist in the new country's set.
              onFieldChange('type', '');
            }}
          />

          <SelectField
            name="industryType"
            label={t('accountSetup.industry')}
            placeholder={t('accountSetup.industryPlaceholder')}
            required
            disabled={isLoading}
            options={industryOptions}
            value={values.industryType}
            error={showError('industryType')}
            onChange={(value) => onFieldChange('industryType', value)}
          />
        </div>

        <SelectField
          name="type"
          label={t('accountSetup.businessType')}
          placeholder={
            values.country
              ? t('accountSetup.businessTypePlaceholder')
              : t('accountSetup.selectCountryFirst')
          }
          required
          disabled={isLoading || !values.country}
          options={businessTypeOptions}
          value={values.type}
          error={showError('type')}
          onChange={(value) => onFieldChange('type', value)}
        />
      </section>

      <section className="p-6 space-y-5 bg-white rounded-lg shadow-sm">
        <header className="space-y-1">
          <h2 className="text-lg font-semibold">
            {t('accountSetup.contactAndLocation')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('accountSetup.optionalFieldsHint')}
          </p>
        </header>

        <div className="grid gap-5 sm:grid-cols-2">
          <InputField
            name="email"
            type="email"
            label={t('accountSetup.businessEmail')}
            placeholder={accountEmail ?? t('accountSetup.businessEmail')}
            helperText={t('accountSetup.businessEmailHelper')}
            value={values.email}
            error={showError('email')}
            onChange={(event) => onFieldChange('email', event.target.value)}
          />

          <InputField
            name="phoneNumber"
            type="tel"
            label={t('accountSetup.phoneNumber')}
            placeholder={phonePlaceholder}
            value={values.phoneNumber}
            error={showError('phoneNumber')}
            onChange={(event) =>
              onFieldChange('phoneNumber', event.target.value)
            }
          />
        </div>

        <InputField
          name="website"
          type="url"
          label={t('accountSetup.website')}
          placeholder="https://example.com"
          value={values.website}
          error={showError('website')}
          onChange={(event) => onFieldChange('website', event.target.value)}
        />

        <InputField
          name="address"
          label={t('accountSetup.streetAddress')}
          value={values.address}
          error={showError('address')}
          onChange={(event) => onFieldChange('address', event.target.value)}
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <InputField
            name="city"
            label={t('accountSetup.city')}
            value={values.city}
            error={showError('city')}
            onChange={(event) => onFieldChange('city', event.target.value)}
          />

          <InputField
            name="zipCode"
            label={postalLabel}
            value={values.zipCode}
            error={showError('zipCode')}
            onChange={(event) => onFieldChange('zipCode', event.target.value)}
          />
        </div>
      </section>

      <Card className="border-none shadow-none bg-white/60">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">
            {t('accountSetup.finishLaterHint')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
